import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { userTripGroupsService } from './reportingService';
import selectTripGroupsModal from './selectTripGroupsModal';
import _ from 'lodash';

interface TripGroup {
  key: string;
  displayName: string;
  parent?: string;
}

interface TripGroupSelectorProps {
  className?: string;
  onChange: (tripGroupIds: string[]) => void;
  resetTripGroups?: boolean;
  setResetTripGroups?: (value: boolean) => void;
}

const TripGroupSelector = forwardRef<any, TripGroupSelectorProps>(({ className, onChange, resetTripGroups, setResetTripGroups }, ref) => {
  const [tripGroups, setTripGroups] = useState<TripGroup[]>([]);
  const [allTripGroups, setAllTripGroups] = useState<TripGroup[]>([]);
  const [includeArchiveGroups, setIncludeArchiveGroups] = useState(false);
  const [responseData, setResponseData] = useState<any[]>([]);

  useEffect(() => {
    loadTripGroups();
  }, []);

  useEffect(() => {
    if(resetTripGroups) {
      reset(allTripGroups);
      setResetTripGroups?.(false);
    }
  }, [resetTripGroups]);
  const loadTripGroups = async () => {
    try {
      const response = await userTripGroupsService.userTripGroups();
      const filteredGroups = _(response.data)
        .filter((group: any) => group.leaf && group.active)
        .map((group: any) => ({
          key: group.id,
          displayName: group.displayName,
        }))
        .sortBy('displayName')
        .value();
      setAllTripGroups(filteredGroups);
      setResponseData(response.data);
      reset(filteredGroups);
    } catch (error) {
      console.error('Error loading trip groups:', error);
    }
  };

  const reset = (groups: TripGroup[]) => {
    setIncludeArchiveGroups(false);
    setTripGroups(groups);
    updateTripGroupIds(groups);
  };

  useImperativeHandle(ref, () => ({
    reset: () => reset(allTripGroups),
  }));

  const selectTripGroups = async () => {
    const currentSelectedTripGroupIds = tripGroups.map(group => group.key);
    try {
      const result = await selectTripGroupsModal(
        currentSelectedTripGroupIds,
        includeArchiveGroups,
        responseData,
      );
      setIncludeArchiveGroups(result.includeArchiveGroups);
      setTripGroups(result.tripGroups);
      updateTripGroupIds(result.tripGroups);
    } catch (error) {
      console.error('Error selecting trip groups:', error);
    }
  };

  const updateTripGroupIds = (groups: TripGroup[]) => {
    const rootTripGroup = groups.find(
      group => !group.parent || group.parent === '#',
    );
    const rootTripGroupSelected = rootTripGroup !== undefined;
    const tripGroupIds =
      groups.length && !rootTripGroupSelected
        ? groups.map(group => group.key)
        : [];
    onChange(tripGroupIds);
  };

  return (
    <div className={className} data-fs-exclude>
      <label htmlFor="trip-groups" className="db-text">
        Trip Groups
      </label>
      <div
        id="trip-groups"
        className="trip-group-selector fs-exclude"
        onClick={selectTripGroups}
        data-fs-exclude
      >
        {tripGroups.map(group => (
          <div key={group.key} className="truncate fs-exclude" title={group.displayName} data-fs-exclude>
            {group.displayName} 
          </div>
        ))}
      </div>
    </div>
  );
});

export default TripGroupSelector;